import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, finalize, Observable, of, shareReplay, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { map } from 'rxjs/operators';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import KeycloakLoginErrorResponse from '../../dtos/keycloak-login-response-error.dto';
import { KeycloakLoginResponse } from '../../dtos/keycloak-login-response.dto';
import { KeycloakAccessTokenPayload, KeycloakRefreshTokenPayload } from '../../dtos/keycloak-jwt-payload.dto';
import { LogoutConfigDto } from '../../dtos/logout-config.dto';
import { FeatureFlagService } from "../feature-flag/feature-flag.service";



/**
 * Login de teste dev: 'admin@simcaq.com.br'
 * Senha de teste dev: 'Nac@educacao2025'
 */
@Injectable({
  providedIn: 'root'
})
export class AutenticacaoService {

  /**
   * Carrega a chave do item da local storage que o usuário utilizou para se logar
   * @private
   */
  private readonly USUARIO_LOGADO = 'usuarioLogado';

  private readonly ACCESS_TOKEN = 'accessToken'

  private readonly REFRESH_TOKEN = 'refreshToken'

  private refreshTokenRequest$: Observable<boolean> | null = null;

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  private realmUrl = `${environment.auth?.url ?? ''}/realms/${environment.auth?.realm ?? ''}`;

  private logoutEvent = new Subject<void>();

  constructor(private http: HttpClient, private router: Router, private ffService: FeatureFlagService) {}

  login(email: string, senha: string): Observable<boolean> {

    if (!this.isBrowser()) {
      return of(false);
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if(!this.ffService.funcionalidadeHabilitada('585-autenticacao-keycloak')){
      if (email === 'admin@simcaq.com.br' && senha === 'Nac@educacao2025') {
        localStorage.setItem(this.USUARIO_LOGADO, email);
        return of(true);
      }
      return of(false);
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const clientId = environment.auth.clientId

    const formUrlEncodedParams = new HttpParams()
      .set('grant_type','password')
      .set('client_id',clientId)
      .set('username',email)
      .set('password',senha);

    return this.http.post<KeycloakLoginResponse>(`${this.realmUrl}/protocol/openid-connect/token`,formUrlEncodedParams.toString(),{
      headers : {
        'Content-Type' : 'application/x-www-form-urlencoded'
      }
    }).pipe(
      map(res=> {

        this.tratarRetornoDeSucessoAccessToken(res)

        return true

      }),
      catchError((err) => {

        this.tratarRetornoDeErroAccessToken(err)

        return of(false);
      })
    )

  }

  refreshToken(): Observable<boolean>{

    const refresh = localStorage.getItem(this.REFRESH_TOKEN)

    if(!refresh){ // jwt expirado sem refresh token
      return of(false)
    }

    const refreshDecoded: KeycloakRefreshTokenPayload =  jwtDecode(refresh)

    if((refreshDecoded?.exp ?? 0) * 1000 < new Date().getTime() ){
      return of(false)
    }

    if(this.refreshTokenRequest$){
      return this.refreshTokenRequest$
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const clientId = environment.auth.clientId

    const params = new HttpParams()
      .set('grant_type','refresh_token')
      .set('client_id',clientId)
      .set('refresh_token',refresh);

    this.refreshTokenRequest$ = this.http.post<KeycloakLoginResponse>(
      `${this.realmUrl}/protocol/openid-connect/token`,
      params.toString(),
      { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
    ).pipe(
      map((res) => {
        this.tratarRetornoDeSucessoAccessToken(res);
        return true;
      }),
      catchError((err) => {
        this.tratarRetornoDeErroAccessToken(err);
        return of(false);
      }),
      // Compartilha a resposta (e só faz 1 requisição para todos os subscribers)
      shareReplay(1),
      // Reseta o "cache" quando finalize para nova chamada futura
      finalize(() => {
        this.refreshTokenRequest$ = null;
      })
    );

    return this.refreshTokenRequest$;

  }

  logout( config: LogoutConfigDto = {redirect: true} ): void {

    if (!this.isBrowser()) {
      return
    }

    localStorage.removeItem(this.USUARIO_LOGADO)

    localStorage.removeItem(this.ACCESS_TOKEN)

    localStorage.removeItem(this.REFRESH_TOKEN)

    this.logoutEvent.next()

    if(config.redirect)
      this.router.navigate(['/'])

  }

  obterAccessToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN)
  }

  isLogado(): boolean {

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if(!this.ffService.funcionalidadeHabilitada('585-autenticacao-keycloak')){
      return !!(localStorage.getItem(this.USUARIO_LOGADO)?.trim())
    }

    const jwt = localStorage.getItem(this.ACCESS_TOKEN);

    if(!jwt) { // token nao existe -> deslogado
      return false
    }

    const jwtDecodado: KeycloakAccessTokenPayload = jwtDecode(jwt)

    return (jwtDecodado?.exp ?? 0) * 1000 >= new Date().getTime();

  }

  isLogadoRefreshSeNao(): Observable<boolean> {

    if (!this.isBrowser()) {
      return of(false);
    }

    if(this.isLogado()){
      return of(true);
    }

    return this.refreshToken();

  }


  private tratarRetornoDeSucessoAccessToken(res: KeycloakLoginResponse): void {

    const jwtDecodado: KeycloakAccessTokenPayload = jwtDecode(res.access_token)

    localStorage.setItem(this.ACCESS_TOKEN,res.access_token)

    localStorage.setItem(this.REFRESH_TOKEN,res.refresh_token)

    localStorage.setItem(this.USUARIO_LOGADO, jwtDecodado.email)

  }

  /**
   * Realiza logout sem redirect
   * @param err erro lançado (geralmente algum http error response)
   * @private
   */
  private tratarRetornoDeErroAccessToken(err: any): void {

    const statusCode = err.status;

    if(statusCode === 401){ //sem auth

      const jsonDeErro: KeycloakLoginErrorResponse = err.error;

      jsonDeErro && console.error(jsonDeErro.error_description);

    }

    if(statusCode === 503 || statusCode === 404){
      console.warn('Servidor de login fora do ar!')
      return
    }

    console.error("Erro ao realizar chamada de keycloak")

    this.logout({redirect: false})

  }

  private isBrowser(): boolean {
    return typeof window !== 'undefined';
  }

  onLogout(): Observable<void> {
    return this.logoutEvent.asObservable();
  }

}
